/**
 * When automatic payout depends on a service outside the game publisher,
 * show a clear, plain-language callout so players know before they deposit.
 */
export default function AutomationThirdPartyNotice({ gameId, variant = 'light', compact = false }) {
  const id = Number(gameId);
  if (id !== 1 && id !== 4) return null;

  const isDark = variant === 'dark';

  const boxStyle = compact
    ? {
        marginTop: 10,
        padding: '12px 14px',
        borderRadius: 8,
        fontSize: '0.88rem',
        lineHeight: 1.55,
        border: isDark ? '1px solid rgba(251, 191, 36, 0.45)' : '1px solid #fcd34d',
        background: isDark ? 'rgba(251, 191, 36, 0.12)' : '#fffbeb',
        color: isDark ? '#fde68a' : '#78350f',
      }
    : {
        marginTop: 0,
        marginBottom: '1.1em',
        padding: '14px 16px',
        borderRadius: 8,
        fontSize: '0.92rem',
        lineHeight: 1.6,
        border: '1px solid #fcd34d',
        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        color: '#422006',
      };

  const titleStyle = {
    margin: compact ? '0 0 8px 0' : '0 0 10px 0',
    fontSize: compact ? '0.92rem' : '0.98rem',
    fontWeight: 700,
    color: isDark ? '#fcd34d' : '#92400e',
  };

  const linkStyle = {
    color: isDark ? '#fde047' : '#b45309',
    fontWeight: 600,
    textDecoration: 'underline',
  };

  if (id === 1) {
    return (
      <div style={boxStyle} role="note" aria-label="Third-party account required for CS2 auto-payout">
        <p style={titleStyle}>Auto-payout needs FACEIT (third-party)</p>
        {compact ? (
          <p style={{ margin: 0 }}>
            We can only detect the winner automatically when you play on{' '}
            <strong>FACEIT</strong> — a separate platform from Valve — and both players add their{' '}
            <strong>FACEIT IDs</strong> below. Ordinary Steam / CS2 matchmaking is{' '}
            <strong>not</strong> verified automatically; use both players confirming the winner instead.
          </p>
        ) : (
          <>
            <p style={{ margin: '0 0 10px 0' }}>
              <strong>FACEIT</strong> is an independent matchmaking platform (not Valve). For{' '}
              <strong>automatic</strong> payout, both challengers need FACEIT accounts, must play the wagered
              match on FACEIT, and must enter each person&apos;s FACEIT nickname/ID here.
            </p>
            <p style={{ margin: 0 }}>
              If you only play in <strong>Steam Counter-Strike</strong> matchmaking, we cannot confirm the
              result automatically yet — you can still complete the challenge by{' '}
              <strong>both agreeing on the winner</strong> in the app.
            </p>
            <p style={{ margin: '10px 0 0 0', fontSize: '0.88em', opacity: 0.95 }}>
              Create or manage a FACEIT account at{' '}
              <a href="https://www.faceit.com/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
                faceit.com
              </a>
              .
            </p>
          </>
        )}
      </div>
    );
  }

  // Rocket League — Ballchasing
  return (
    <div style={boxStyle} role="note" aria-label="Third-party service required for Rocket League auto-payout">
      <p style={titleStyle}>Auto-payout needs Ballchasing (third-party)</p>
      {compact ? (
        <p style={{ margin: 0 }}>
          We use <strong>Ballchasing</strong> (ballchasing.com), a replay site <strong>not</strong> run by Epic
          Games. You need a <strong>free Ballchasing account</strong> to upload replays from{' '}
          <strong>PC</strong>. Console replays usually cannot be uploaded the same way.
        </p>
      ) : (
        <>
          <p style={{ margin: '0 0 10px 0' }}>
            <strong>Ballchasing</strong> (ballchasing.com) is a separate service from Epic Games / Rocket
            League. Automatic payout works by reading your match from replays stored there — typically after
            you sign up (free) and <strong>upload the replay file from PC</strong>.
          </p>
          <p style={{ margin: 0 }}>
            Your <strong>Epic Games display name</strong> here must match the name on the replay. Without a
            Ballchasing upload, we cannot pay out automatically; you can still finish the challenge by{' '}
            <strong>both players confirming the winner</strong>.
          </p>
          <p style={{ margin: '10px 0 0 0', fontSize: '0.88em', opacity: 0.95 }}>
            Upload replays at{' '}
            <a href="https://ballchasing.com/upload" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              ballchasing.com/upload
            </a>{' '}
            —{' '}
            <a href="https://ballchasing.com/doc/faq" target="_blank" rel="noopener noreferrer" style={linkStyle}>
              Ballchasing FAQ
            </a>
            .
          </p>
        </>
      )}
    </div>
  );
}
